import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Trash2, Clock, Flame, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { getFoodById } from '../utils/nutritionCalculator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MEAL_ICONS = ['🌅', '🥤', '☀️', '🍎', '🌙'];
const MEAL_NAMES = ['Comida 1', 'Comida 2', 'Comida 3', 'Comida 4', 'Comida 5'];
const MEAL_TIMES = ['7:00 AM', '10:00 AM', '1:00 PM', '4:00 PM', '7:30 PM'];

const RecipeGenerator = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plan');
  const [savedMenus, setSavedMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [collection, setCollection] = useState([]);
  const [loadingCollection, setLoadingCollection] = useState(false);
  const [generatingMeal, setGeneratingMeal] = useState(null);
  const [generatedRecipes, setGeneratedRecipes] = useState({});
  const [expandedRecipe, setExpandedRecipe] = useState(null);
  const [savedRecipeIds, setSavedRecipeIds] = useState(new Set());
  const [successMessage, setSuccessMessage] = useState(null);
  const [generationTimer, setGenerationTimer] = useState(0);

  useEffect(() => { init(); }, []);

  const init = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) { navigate('/login'); return; }
    try {
      const [userRes, menusRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/nutrition-form/my-menus`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUser(userRes.data);
      if (userRes.data.subscription_level !== 'premium') {
        navigate('/dashboard');
        toast.error('Solo disponible para Club VIP Premium');
        return;
      }
      const menus = menusRes.data?.menus || [];
      setNutritionPlan(menusRes.data?.nutrition_plan || null);
      setSavedMenus(menus);
      if (menus.length > 0) setSelectedMenu(0);
      fetchCollection();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCollection = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    setLoadingCollection(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recipes/my-collection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollection(res.data.recipes || []);
      setSavedRecipeIds(new Set((res.data.recipes || []).map(r => r.recipe_id)));
    } catch (error) {
      console.error('Error loading collection:', error);
    } finally {
      setLoadingCollection(false);
    }
  };

  const getMealFoods = useCallback((mealIndex, menu) => {
    const foods = menu?.selected_foods || menu?.foods || {};
    const proteinId = foods[`meal_${mealIndex}_protein`] || foods[`meal_${mealIndex}_proteina`];
    const carbId = foods[`meal_${mealIndex}_carbs`] || foods[`meal_${mealIndex}_carbohidrato`] || foods[`meal_${mealIndex}_carbo`];
    const fatId = foods[`meal_${mealIndex}_fat`] || foods[`meal_${mealIndex}_grasa`];
    const fruitId = foods[`meal_${mealIndex}_fruit`] || foods[`meal_${mealIndex}_fruta`];
    return {
      protein: getFoodById('proteina', proteinId),
      carb: getFoodById('carbohidrato', carbId),
      fat: getFoodById('grasa', fatId),
      fruit: getFoodById('fruta', fruitId)
    };
  }, []);

  const getMealFoodNames = (mealIndex, menu) => {
    const { protein, carb, fat, fruit } = getMealFoods(mealIndex, menu);
    const names = [];
    if (protein) names.push(protein.nombre || protein.name);
    if (carb) names.push(carb.nombre || carb.name);
    if (fat) names.push(fat.nombre || fat.name);
    if (fruit) names.push(fruit.nombre || fruit.name);
    names.push('Vegetales');
    return names;
  };

  const generateRecipes = async (mealIndex) => {
    const menu = savedMenus[selectedMenu];
    if (!menu) return;
    const foodNames = getMealFoodNames(mealIndex, menu);
    const macros = menu.nutrition_plan?.macros_por_comida || nutritionPlan?.macros_por_comida || {};
    const caloriesPerMeal = menu.nutrition_plan?.calorias_por_comida || nutritionPlan?.calorias_por_comida || 0;

    setGeneratingMeal(mealIndex);
    setGenerationTimer(0);
    const token = localStorage.getItem('session_token');
    
    const timerInterval = setInterval(() => {
      setGenerationTimer(prev => prev + 1);
    }, 1000);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/recipes/generate`, {
        foods: foodNames,
        meal_name: MEAL_NAMES[mealIndex] || `Comida ${mealIndex + 1}`,
        macros,
        calories: caloriesPerMeal
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 });

      const batchId = res.data.batch_id;
      if (!batchId) throw new Error('No batch_id');

      // Poll until ALL done (recipes + images together)
      await new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 50; // 150s max
        const poll = setInterval(async () => {
          attempts++;
          try {
            const statusRes = await axios.get(`${BACKEND_URL}/api/recipes/status/${batchId}`);
            const { status, recipes } = statusRes.data;
            
            if (status === 'done' && recipes && recipes.length > 0) {
              clearInterval(poll);
              setGeneratedRecipes(prev => ({ ...prev, [mealIndex]: recipes }));
              resolve();
            } else if (status === 'error' || attempts >= maxAttempts) {
              clearInterval(poll);
              if (recipes && recipes.length > 0) {
                setGeneratedRecipes(prev => ({ ...prev, [mealIndex]: recipes }));
              } else {
                toast.error('Error generando recetas. Intenta de nuevo.');
              }
              resolve();
            }
          } catch (e) {
            if (attempts >= maxAttempts) {
              clearInterval(poll);
              toast.error('Error de conexión. Intenta de nuevo.');
              resolve();
            }
          }
        }, 3000);
      });
    } catch (error) {
      toast.error('Error generando recetas. Intenta de nuevo.');
    } finally {
      clearInterval(timerInterval);
      setGeneratingMeal(null);
      setGenerationTimer(0);
    }
  };

  const saveRecipe = async (recipe) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.post(`${BACKEND_URL}/api/recipes/save`, { recipe }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedRecipeIds(prev => new Set([...prev, recipe.recipe_id]));
      setCollection(prev => [{ ...recipe, saved_at: new Date().toISOString() }, ...prev]);
      setSuccessMessage(`"${recipe.name}" guardada en tu colección`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      toast.error('Error al guardar receta');
    }
  };

  const deleteRecipe = async (recipeId) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/recipes/${recipeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollection(prev => prev.filter(r => r.recipe_id !== recipeId));
      setSavedRecipeIds(prev => { const n = new Set(prev); n.delete(recipeId); return n; });
      toast.success('Receta eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const numMeals = nutritionPlan?.comidas_por_dia || savedMenus[selectedMenu]?.nutrition_plan?.comidas_por_dia || 5;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0c0a09] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0c0a09] py-24 px-4" data-testid="recipe-generator-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-amber-400 text-sm font-bold tracking-widest">CLUB VIP PREMIUM</span>
          </div>
          <h1 className="font-barlow text-3xl md:text-5xl font-black text-white mb-2" data-testid="recipe-title">
            Generador de Recetas
          </h1>
          <p className="text-zinc-500 text-base">Transforma tu plan con recetas infinitas</p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-3 mb-6">
          <button
            onClick={() => setActiveTab('plan')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all border ${
              activeTab === 'plan' ? 'bg-white text-[#0c0a09] border-white' : 'bg-transparent text-zinc-400 border-zinc-700 hover:border-zinc-500'
            }`}
            data-testid="tab-plan"
          >
            <BookOpen className="w-4 h-4" /> Mi Plan
          </button>
          <button
            onClick={() => { setActiveTab('collection'); fetchCollection(); }}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all border ${
              activeTab === 'collection' ? 'bg-white text-[#0c0a09] border-white' : 'bg-transparent text-zinc-400 border-zinc-700 hover:border-zinc-500'
            }`}
            data-testid="tab-collection"
          >
            <span>📁</span> Mi Colección
            {collection.length > 0 && (
              <span className="bg-amber-500 text-black text-xs font-black rounded-full w-5 h-5 flex items-center justify-center">{collection.length}</span>
            )}
          </button>
        </div>

        {/* Success toast */}
        {successMessage && (
          <div className="bg-green-500/15 border border-green-500/30 text-green-400 rounded-xl px-4 py-3 mb-6 text-center text-sm font-medium animate-in fade-in" data-testid="success-message">
            {successMessage}
          </div>
        )}

        {/* ===== TAB: MI PLAN ===== */}
        {activeTab === 'plan' && (
          <div>
            {savedMenus.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center">
                <p className="text-zinc-400 text-lg mb-4">No tienes menús guardados aún</p>
                <Button onClick={() => navigate('/nutrition-form-premium')} className="bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-full px-8">
                  Crear mi Plan Nutricional
                </Button>
              </div>
            ) : (
              <>
                {savedMenus.length > 1 && (
                  <div className="flex gap-2 mb-6 flex-wrap">
                    {(() => {
                      let altoCount = 0, bajoCount = 0;
                      return savedMenus.map((menu, idx) => {
                        const label = menu.menu_type === 'masa' 
                          ? `Día Alto ${++altoCount}` 
                          : `Día Bajo ${++bajoCount}`;
                        return (
                          <button key={idx} onClick={() => { setSelectedMenu(idx); setGeneratedRecipes({}); }}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                              selectedMenu === idx ? 'bg-amber-500/20 text-amber-400 border-amber-500/50' : 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:border-zinc-500'
                            }`}>
                            {menu.menu_type === 'masa' ? '💪' : '🔥'} {label}
                          </button>
                        );
                      });
                    })()}
                  </div>
                )}

                {selectedMenu !== null && savedMenus[selectedMenu] && (
                  <div className="space-y-4">
                    {Array.from({ length: numMeals }, (_, i) => {
                      const menu = savedMenus[selectedMenu];
                      const foodDetails = getMealFoods(i, menu);
                      const foodNames = getMealFoodNames(i, menu);
                      const macros = menu.nutrition_plan?.macros_por_comida || nutritionPlan?.macros_por_comida || {};
                      const calsPerMeal = menu.nutrition_plan?.calorias_por_comida || nutritionPlan?.calorias_por_comida || 0;
                      const hasAnyFood = foodDetails.protein || foodDetails.carb || foodDetails.fat || foodDetails.fruit;

                      return (
                        <div key={i}>
                          <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-5" data-testid={`meal-card-${i}`}>
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <span className="text-2xl">{MEAL_ICONS[i] || '🍽️'}</span>
                                  <div>
                                    <h3 className="text-white font-bold text-lg">{MEAL_NAMES[i] || `Comida ${i + 1}`}</h3>
                                    <p className="text-zinc-500 text-xs">{MEAL_TIMES[i] || ''} · {calsPerMeal} kcal</p>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5 mt-3">
                                  {foodDetails.protein && <span className="bg-zinc-800 text-zinc-300 text-xs px-2.5 py-1 rounded-full border border-zinc-700">{foodDetails.protein.nombre || foodDetails.protein.name}</span>}
                                  {foodDetails.carb && <span className="bg-zinc-800 text-zinc-300 text-xs px-2.5 py-1 rounded-full border border-zinc-700">{foodDetails.carb.nombre || foodDetails.carb.name}</span>}
                                  {foodDetails.fat && <span className="bg-zinc-800 text-zinc-300 text-xs px-2.5 py-1 rounded-full border border-zinc-700">{foodDetails.fat.nombre || foodDetails.fat.name}</span>}
                                  {foodDetails.fruit && <span className="bg-zinc-800 text-zinc-300 text-xs px-2.5 py-1 rounded-full border border-zinc-700">{foodDetails.fruit.nombre || foodDetails.fruit.name}</span>}
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="text-xs space-x-2 mb-3">
                                  <span className="text-red-400 font-bold">{macros.proteinas || 0}g</span>
                                  <span className="text-zinc-500">Prot</span>
                                  <span className="text-amber-400 font-bold">{macros.carbohidratos || 0}g</span>
                                  <span className="text-zinc-500">Carb</span>
                                  <span className="text-yellow-400 font-bold">{macros.grasas || 0}g</span>
                                  <span className="text-zinc-500">Gras</span>
                                </div>
                                {hasAnyFood && (
                                  <button onClick={() => generateRecipes(i)} disabled={generatingMeal !== null}
                                    className="bg-transparent border border-zinc-600 text-white hover:border-amber-500 hover:text-amber-400 px-4 py-2 rounded-lg text-sm font-bold transition-all disabled:opacity-50"
                                    data-testid={`generate-recipes-btn-${i}`}>
                                    {generatingMeal === i ? `Generando... ${generationTimer}s` : generatingMeal !== null ? 'Espera...' : '✦ Generar Recetas'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Loading overlay while generating */}
                          {generatingMeal === i && (
                            <div className="bg-zinc-900/90 border border-amber-500/20 rounded-2xl p-8 mt-4 text-center" data-testid={`generating-overlay-${i}`}>
                              <div className="w-12 h-12 border-3 border-amber-500/20 border-t-amber-500 rounded-full animate-spin mx-auto mb-4"></div>
                              <p className="text-white font-bold text-lg mb-1">Creando tus recetas con fotos reales</p>
                              <p className="text-zinc-400 text-sm mb-3">Nuestro chef esta preparando 3 platos unicos para ti</p>
                              <div className="bg-zinc-800 rounded-full h-2 w-48 mx-auto overflow-hidden">
                                <div className="bg-amber-500 h-full rounded-full transition-all duration-1000" style={{ width: `${Math.min(generationTimer * 1.4, 95)}%` }}></div>
                              </div>
                              <p className="text-zinc-500 text-xs mt-2">Tiempo estimado: ~60 segundos ({generationTimer}s)</p>
                            </div>
                          )}

                          {/* Generated recipes */}
                          {generatedRecipes[i] && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                              {generatedRecipes[i].map((recipe, rIdx) => (
                                <RecipeCard
                                  key={rIdx}
                                  recipe={recipe}
                                  mealIdx={i}
                                  recipeIdx={rIdx}
                                  isSaved={savedRecipeIds.has(recipe.recipe_id)}
                                  isExpanded={expandedRecipe === `${i}-${rIdx}`}
                                  onToggleExpand={() => setExpandedRecipe(expandedRecipe === `${i}-${rIdx}` ? null : `${i}-${rIdx}`)}
                                  onSave={() => saveRecipe(recipe)}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ===== TAB: MI COLECCIÓN ===== */}
        {activeTab === 'collection' && (
          <div>
            {loadingCollection ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
              </div>
            ) : collection.length === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center" data-testid="empty-collection">
                <p className="text-zinc-300 text-lg font-bold mb-2">Tu colección está vacía</p>
                <p className="text-zinc-500 text-sm mb-6">Genera recetas desde tu plan y guárdalas aquí</p>
                <button onClick={() => setActiveTab('plan')} className="text-amber-400 hover:text-amber-300 text-sm font-medium">
                  ← Ir a mi Plan
                </button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="collection-list">
                {collection.map((recipe) => (
                  <CollectionRecipeCard key={recipe.recipe_id} recipe={recipe} onDelete={deleteRecipe} />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-10 text-center">
          <button onClick={() => navigate('/nutrition-form-premium')} className="text-zinc-500 hover:text-amber-400 text-sm font-medium transition-colors">
            ← Volver a mi Plan Nutricional
          </button>
        </div>
      </div>
    </div>
  );
};

/* ========== Recipe Card (Generated) ========== */
const RecipeCard = ({ recipe, mealIdx, recipeIdx, isSaved, isExpanded, onToggleExpand, onSave }) => {
  const nutrition = recipe.nutrition || {};

  return (
    <div className="bg-zinc-900/80 border border-zinc-700/60 rounded-2xl overflow-hidden flex flex-col" data-testid={`recipe-card-${mealIdx}-${recipeIdx}`}>
      {/* Image */}
      <div className="aspect-[16/10] bg-zinc-800 relative overflow-hidden rounded-t-2xl">
        {recipe.image ? (
          <img src={recipe.image} alt={recipe.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-zinc-800 via-zinc-700/80 to-zinc-800 gap-1">
            <span className="text-3xl">🍽️</span>
          </div>
        )}
        {/* Tag overlay */}
        <div className="absolute top-2 left-2">
          <span className="bg-black/70 backdrop-blur-sm text-amber-400 text-[10px] font-black tracking-widest px-2.5 py-1 rounded-full uppercase">
            {recipe.tag}
          </span>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h4 className="text-white font-bold text-sm leading-tight mb-1">{recipe.name}</h4>
        <p className="text-zinc-500 text-xs mb-3 line-clamp-2">{recipe.description}</p>

        {/* Quick info */}
        <div className="flex items-center gap-2 text-[11px] text-zinc-400 mb-2 flex-wrap">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {recipe.time_minutes} min</span>
          <span className="text-zinc-600">·</span>
          <span>🍳 {recipe.difficulty}</span>
          <span className="text-zinc-600">·</span>
          <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> {recipe.calories} kcal</span>
        </div>

        {/* Macro pills */}
        {(nutrition.protein || nutrition.carbs || nutrition.fat) && (
          <div className="flex gap-1.5 mb-3">
            <span className="bg-red-500/10 text-red-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-red-500/20">
              P {nutrition.protein}g
            </span>
            <span className="bg-amber-500/10 text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-500/20">
              C {nutrition.carbs}g
            </span>
            <span className="bg-yellow-500/10 text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-yellow-500/20">
              G {nutrition.fat}g
            </span>
          </div>
        )}

        {/* Expand toggle */}
        <button onClick={onToggleExpand}
          className="w-full flex items-center justify-center gap-2 bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 rounded-lg py-2 text-xs font-medium transition-colors mb-3">
          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {isExpanded ? 'Ocultar' : 'Ver detalle'}
        </button>

        {/* Expanded detail */}
        {isExpanded && (
          <div className="bg-zinc-800/50 rounded-xl p-3 mb-3 text-xs space-y-3 border border-zinc-700/50">
            <div>
              <p className="font-bold text-zinc-200 mb-1.5 flex items-center gap-1">🧂 Ingredientes</p>
              <ul className="text-zinc-400 space-y-0.5">
                {recipe.ingredients.map((ing, idx) => (
                  <li key={idx} className="flex items-start gap-1.5">
                    <span className="text-amber-500 mt-0.5">•</span>
                    <span>{ing}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-bold text-zinc-200 mb-1.5 flex items-center gap-1">📋 Preparación</p>
              <div className="text-zinc-400 space-y-1">
                {recipe.steps.map((step, idx) => (
                  <p key={idx} className="leading-relaxed">{step}</p>
                ))}
              </div>
            </div>
            {recipe.tip && (
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5">
                <p className="text-amber-400 text-xs font-medium">💡 {recipe.tip}</p>
              </div>
            )}
          </div>
        )}

        {/* Save button */}
        <button onClick={() => !isSaved && onSave()} disabled={isSaved}
          className={`w-full flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold transition-all mt-auto border ${
            isSaved ? 'bg-zinc-800 text-zinc-500 border-zinc-700 cursor-default' : 'bg-transparent text-white border-zinc-600 hover:border-amber-500 hover:text-amber-400'
          }`}
          data-testid={`save-recipe-btn-${mealIdx}-${recipeIdx}`}>
          {isSaved ? '✓ Guardada' : '📁 Guardar en colección'}
        </button>
      </div>
    </div>
  );
};

/* ========== Collection Recipe Card ========== */
const CollectionRecipeCard = ({ recipe, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const nutrition = recipe.nutrition || {};

  return (
    <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl overflow-hidden" data-testid={`collection-recipe-${recipe.recipe_id}`}>
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        {recipe.image && (
          <div className="sm:w-48 h-40 sm:h-auto flex-shrink-0">
            <img src={recipe.image} alt={recipe.name} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="flex-1 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <span className="text-[10px] font-black tracking-widest text-amber-400 uppercase">{recipe.tag}</span>
              <h4 className="text-white font-bold text-base mt-1">{recipe.name}</h4>
              <p className="text-zinc-500 text-xs mt-1">{recipe.description}</p>
              <div className="flex items-center gap-3 text-xs text-zinc-400 mt-2 flex-wrap">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {recipe.time_minutes} min</span>
                <span>🍳 {recipe.difficulty}</span>
                <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> {recipe.calories} kcal</span>
                {recipe.meal_name && <span className="text-zinc-600">| {recipe.meal_name}</span>}
              </div>
              {(nutrition.protein || nutrition.carbs || nutrition.fat) && (
                <div className="flex gap-1.5 mt-2">
                  <span className="bg-red-500/10 text-red-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-red-500/20">P {nutrition.protein}g</span>
                  <span className="bg-amber-500/10 text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-500/20">C {nutrition.carbs}g</span>
                  <span className="bg-yellow-500/10 text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-yellow-500/20">G {nutrition.fat}g</span>
                </div>
              )}
            </div>
            <button onClick={() => onDelete(recipe.recipe_id)} className="text-zinc-600 hover:text-red-400 p-1 transition-colors" title="Eliminar">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <button onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-center gap-2 bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 rounded-lg py-2 text-xs font-medium transition-colors mt-3">
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? 'Ocultar detalle' : 'Ver detalle'}
          </button>

          {expanded && (
            <div className="bg-zinc-800/50 rounded-xl p-4 mt-3 text-xs space-y-3 border border-zinc-700/50">
              <div>
                <p className="font-bold text-zinc-200 mb-1.5">🧂 Ingredientes</p>
                <ul className="text-zinc-400 space-y-0.5">
                  {recipe.ingredients?.map((ing, idx) => (
                    <li key={idx} className="flex items-start gap-1.5"><span className="text-amber-500 mt-0.5">•</span><span>{ing}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-bold text-zinc-200 mb-1.5">📋 Preparación</p>
                <div className="text-zinc-400 space-y-1">
                  {recipe.steps?.map((step, idx) => <p key={idx} className="leading-relaxed">{step}</p>)}
                </div>
              </div>
              {recipe.tip && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5">
                  <p className="text-amber-400 text-xs font-medium">💡 {recipe.tip}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeGenerator;
